import { JSX } from 'react';
import { UseFormReturn, useFieldArray } from 'react-hook-form';

import { AnimatePresence } from 'framer-motion';

import { AddActionButton } from 'components/AddActionButton';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';

import { CommodityItemInputCard } from './components/CommodityItemInputCard';
import { PlsFormValues } from './types';
import { createDefaultPlsCommodity } from './utils';

type LoadItemsSectionProps = {
  formMethods: UseFormReturn<PlsFormValues>;
  isReadOnly?: boolean;
};

export function LoadItemsSection({
  formMethods,
  isReadOnly = false,
}: LoadItemsSectionProps): JSX.Element {
  const {
    control,
    watch,
    clearErrors,
    formState: { errors },
  } = formMethods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'commodities',
  });

  const watchedCommodities = watch('commodities');
  const numberOfItems = watchedCommodities?.length || 0;
  const errorMessage = errors.commodities?.message;

  const addCommodity = () => {
    append(createDefaultPlsCommodity());
    clearErrors('commodities');
  };

  return (
    <div className='w-full'>
      {!isReadOnly && (
        <AddActionButton
          onClick={addCommodity}
          buttonText='Add Item'
          descriptionText='Add item(s) for the shipment'
          buttonNamePosthog='add_commodity_item'
          className='mb-2'
        />
      )}

      {errorMessage && (
        <Typography variant='body-xs' className='text-error-500 mb-2'>
          {errorMessage}
        </Typography>
      )}

      <AnimatePresence>
        <Flex
          direction='col'
          gap='md'
          className='w-full mx-0'
          data-testid='commodities-container'
        >
          {fields.map((field, index) => (
            <CommodityItemInputCard
              key={field.id}
              formMethods={formMethods}
              index={index}
              onRemove={() => remove(index)}
              isDeletable={!isReadOnly && fields.length > 1}
            />
          ))}
        </Flex>
      </AnimatePresence>

      {numberOfItems > 0 && (
        <Typography variant='body-sm' className='mt-2 text-neutral-600'>
          Total items: {numberOfItems}
        </Typography>
      )}
    </div>
  );
}
